import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'src/utils/axios';
import type { CaseSummary } from 'src/components/SupportCase/types';
import { readPageContext } from 'src/components/SupportCase/useStartCase';

/** What the panel should show when it opens (ENG-4251, PA-09). */
export type ResumeState =
  | 'loading'
  | 'ready'
  | 'chooser'
  | 'empty'
  | 'error';

export interface CaseResume {
  state: ResumeState;
  /** Open conversations about the page's context, newest first. */
  open: CaseSummary[];
  /** Resolved ones, for "Earlier conversations". */
  earlier: CaseSummary[];
  /** The case to open when there is exactly one open conversation. */
  resumeCaseId: number | null;
  /** The page context the lookup used, for the empty-state sentence. */
  contextLabel: string;
  refetch: () => Promise<void>;
}

const OPEN_STATES = new Set(['agent_active', 'awaiting_user', 'awaiting_approval', 'filed']);

/**
 * Context-scoped resume for the floating panel (ENG-4251, PA-09).
 *
 * Closing the panel is a UI dismissal: the conversation still exists, and reopening on
 * the same page must find it. Exactly one open conversation resumes automatically; more
 * than one is a chooser, because guessing would hide the other thread. Resolved ones
 * are offered explicitly behind "Earlier conversations" — never as the default.
 *
 * The lookup is the case list with a context filter, so there is one history model,
 * not a second one for the panel.
 */
const useCaseResume = (enabled: boolean): CaseResume => {
  const context = useMemo(() => readPageContext(), []);
  const [state, setState] = useState<ResumeState>('loading');
  const [open, setOpen] = useState<CaseSummary[]>([]);
  const [earlier, setEarlier] = useState<CaseSummary[]>([]);

  const load = useCallback(async () => {
    if (!enabled) {
      return;
    }
    if (!context.certificationId && !context.propertySlug) {
      // A page with no context has nothing to resume; the empty state says so.
      setState('empty');
      setOpen([]);
      setEarlier([]);
      return;
    }
    setState('loading');
    try {
      const params: Record<string, string | number> = {};
      if (context.certificationId) {
        params.certification_id = context.certificationId;
      } else if (context.propertySlug) {
        // The list filter takes an id; a slug-only page falls back to the whole list
        // and filters client-side by the property ref's slug, which the API returns.
        params.limit = 100;
      }
      const { data } = await axios.get('/support/cases/', { params });
      let rows = (data.cases || []) as CaseSummary[];
      if (!context.certificationId && context.propertySlug) {
        rows = rows.filter(
          (row) =>
            row.certification === null &&
            row.property?.slug === context.propertySlug
        );
      }
      const openRows = rows.filter((row) => OPEN_STATES.has(row.state));
      setOpen(openRows);
      setEarlier(rows.filter((row) => !OPEN_STATES.has(row.state)));
      if (openRows.length === 1) {
        setState('ready');
      } else if (openRows.length > 1) {
        setState('chooser');
      } else {
        setState('empty');
      }
    } catch {
      setState('error');
    }
  }, [context.certificationId, context.propertySlug, enabled]);

  useEffect(() => {
    void load();
  }, [load]);

  const contextLabel = context.certificationId
    ? 'this certification'
    : context.propertySlug
      ? 'this property'
      : 'this page';

  return {
    state,
    open,
    earlier,
    resumeCaseId: open.length === 1 ? open[0].id : null,
    contextLabel,
    refetch: load,
  };
};

export default useCaseResume;
